import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import { withApollo } from 'react-apollo';
import Modal from '../Global/Modals/Modal';
import TextFieldInput from '../Global/Inputs/TextField';
import OutlinedSelectInput from '../Global/Inputs/OutlinedSelect';
import ChippedAutocomplete from '../Global/Inputs/ChippedAutocomplete';
import CheckboxInput from '../Global/Inputs/Checkbox';
import ProgressButton from '../Global/Buttons/Progress';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import { Formik, Field } from 'formik';
import Grid from '@material-ui/core/Grid';
import GridItem from '../Global/GridItem';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _countBy from 'lodash/countBy';
import { AppContext } from '../../AppProvider';

import { buildDropdown } from '../../utils/Shipment';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import ButtonStyles from '../../styles/button';
import SpacingStyles from '../../styles/helpers/spacing';

import { checkDigitTypes as checkDigitTypesQuery } from '../../graphQL/queries/referenceDataQueries';

import EProFormSchema from './EProForm.schema';
import EProModel from '../../Models/EProNumber';

import EProFormStyles from './__styles__/EProForm.styles';

import { CHECK_DIGIT_TYPE_NONE } from '../../constants/constants';

const FORM_TYPE = {
  SAMPLE: 'SAMPLE',
  PERSIST: 'PERSIST'
};

const FORMAT_LENGTH_PER_CHECK_DIGIT = {
  REDDAWAY: 10,
  SEFL: 8,
  YRC: 9
};

export class EProForm extends PureComponent {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired
  };

  state = {
    checkDigitTypes: [],
    checkDigitType: CHECK_DIGIT_TYPE_NONE,
    format: null,
    eProSample: null,
    formatLengthPerCheckDigit: null,
    locationTyped: '',
    locationsSelected: []
  };

  async componentDidMount() {
    const { client: ApolloClient } = this.props;

    try {
      const {
        data: {
          referenceData: { checkDigitTypes }
        }
      } = await ApolloClient.query({
        query: checkDigitTypesQuery
      });

      this.setState({
        checkDigitTypes: buildDropdown(checkDigitTypes)
      });
    } catch (error) {
      this.setState({
        error
      });
    }
  }

  _handleOnClose = () => {
    this.props.onClose();
  };

  _handleOnSave = async values => {
    const { onSave } = this.props;
    await onSave(values);
  };

  _getLengthAllowed = () => {
    return (
      this.state.formatLengthPerCheckDigit || _countBy(this.state.format)['#']
    );
  };

  _handleValidateField = async (
    currentField,
    currentValue,
    values,
    setFieldError
  ) => {
    const { t } = this.props;
    const checkType = _get(values, 'checkDigitTypeId', null);
    const schema = EProFormSchema({
      t,
      lengthAllowed: this._getLengthAllowed(),
      checkDigitType: checkType
    });

    const valueCopy = { ...values };
    const updatedValues = _set(valueCopy, currentField, currentValue);

    try {
      await schema.validateAt(currentField, updatedValues);
      setFieldError(currentField, null);

      if (currentField === 'format') {
        this.setState({ format: updatedValues.format.replace(/@/, 1) });
      }
      if (currentField === 'sequenceStart') {
        this.setState({ sequenceStart: updatedValues.sequenceStart });
      }
      if (currentField === 'checkDigitTypeId') {
        this.setState({
          formatLengthPerCheckDigit:
            FORMAT_LENGTH_PER_CHECK_DIGIT[updatedValues.checkDigitTypeId],
          checkDigitType: updatedValues.checkDigitTypeId
        });
      }
    } catch (err) {
      if (currentField === 'format') {
        this.setState({ format: null });
      }
      if (currentField === 'sequenceStart') {
        this.setState({ sequenceStart: null });
      }
      setFieldError(currentField, err.message);
    }
  };

  _generateEProSample = () => {
    const { format, sequenceStart } = this.state;

    if (!format) {
      return null;
    }

    let index = 0;
    let eProSample = format;

    if (sequenceStart && sequenceStart.length === _countBy(format)['#']) {
      eProSample = format.replace(/#/g, _ => sequenceStart.toString()[index++]);
    }

    return eProSample;
  };

  _handleChange = (field, value) => {
    this.setState({ [field]: value });
  };

  _chipIt = location => {
    const existingLocationsSelected = [...this.state.locationsSelected];
    existingLocationsSelected.push(location);
    this.setState({
      locationsSelected: existingLocationsSelected,
      locationTyped: ''
    });
  };

  _getSuggestions = async value => {
    const locations = this.props.locations.map(location => ({
      label: location.name,
      value: location.id
    }));
    const locationIdsSelected = [...this.state.locationsSelected].map(
      ls => ls.value
    );
    const locationsSuggestion = locations.filter(
      location => !locationIdsSelected.includes(location.value)
    );
    const suggestions = locationsSuggestion.filter(location =>
      RegExp(value.toLowerCase()).test(location.label.toLowerCase())
    );

    return Promise.resolve(suggestions);
  };

  render() {
    const {
      classes,
      t,
      isSubmitting,
      sampleGenarated = null,
      type = FORM_TYPE.SAMPLE
    } = this.props;

    return (
      <Formik
        validationSchema={EProFormSchema({
          t,
          lengthAllowed: this._getLengthAllowed(),
          checkDigitType: this.state.checkDigitType
        })}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...EProModel()
        }}
        onSubmit={async values => {
          await this._handleOnSave(values);
        }}
        onReset={() => {
          this._handleOnClose();
        }}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          setFieldError,
          dirty
        }) => (
          <Modal
            open={true}
            onClose={handleReset}
            classes={{
              footer: classnames({
                [classes.modalFooterForSample]: Boolean(sampleGenarated)
              })
            }}
            body={
              <Fragment>
                {!sampleGenarated && (
                  <Fragment>
                    <div className={classes.titleContainer}>
                      <p
                        className={classnames(classes.headline6, classes.title)}
                      >
                        {type === FORM_TYPE.PERSIST && t('Add E-PRO Number')}
                        {type === FORM_TYPE.SAMPLE &&
                          t('Generate E-PRO Sample')}
                      </p>
                    </div>
                    <p className={classes.body1Light}>
                      {t(
                        'Include a # sign to represent sequence numbers, and an @ symbol to represent the check digit.'
                      )}
                    </p>
                    <p
                      className={classnames(
                        classes.body1Light,
                        classes.eProPreview
                      )}
                    >
                      {this._generateEProSample() || t('PRO Number Preview')}
                    </p>
                    <Grid container spacing={16}>
                      <GridItem xs={6}>
                        <Field
                          name="format"
                          onChange={handleChange}
                          value={values.format}
                          label={t('Format')}
                          component={TextFieldInput}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      </GridItem>
                      <GridItem xs={6}>
                        <Field
                          name="checkDigitTypeId"
                          onChange={handleChange}
                          value={values.checkDigitTypeId}
                          label={t('Check Digit Function')}
                          options={this.state.checkDigitTypes}
                          component={OutlinedSelectInput}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                            this._handleValidateField(
                              'format',
                              values.format,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      </GridItem>
                      <GridItem xs={6}>
                        <Field
                          name="sequenceStart"
                          onChange={handleChange}
                          component={TextFieldInput}
                          value={values.sequenceStart}
                          label={t('Lower Bound')}
                          type={'number'}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      </GridItem>
                      <GridItem xs={6}>
                        <Field
                          name="sequenceEnd"
                          onChange={handleChange}
                          component={TextFieldInput}
                          value={values.sequenceEnd}
                          label={t('Upper Bound')}
                          type={'number'}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      </GridItem>
                      {type === FORM_TYPE.PERSIST && (
                        <GridItem xs={12}>
                          <div className={classes.checkboxesContainer}>
                            <Field
                              name="locations"
                              render={({ field, form }) => (
                                <ChippedAutocomplete
                                  getSuggestions={this._getSuggestions}
                                  value={this.state.locationTyped}
                                  values={values.locations}
                                  onChange={evt => {
                                    const {
                                      target: { value }
                                    } = evt;
                                    this.setState({ locationsSelected: value });
                                    handleChange(evt);
                                  }}
                                  field={field}
                                  form={form}
                                  label={t('Location(s)')}
                                />
                              )}
                            />
                            <Field
                              name="forInbound"
                              render={({ field, form }) => (
                                <CheckboxInput
                                  label={t('Inbound')}
                                  field={field}
                                  form={form}
                                  onChange={null}
                                  classes={{ root: classes.checkboxes }}
                                />
                              )}
                            />
                            <Field
                              name="forOutbound"
                              onChange={handleChange}
                              render={({ field, form }) => (
                                <CheckboxInput
                                  label={t('Outbound')}
                                  field={field}
                                  form={form}
                                  onChange={null}
                                  classes={{ root: classes.checkboxes }}
                                />
                              )}
                            />
                          </div>
                        </GridItem>
                      )}
                    </Grid>
                  </Fragment>
                )}
                {sampleGenarated && (
                  <Fragment>
                    <div className={classes.titleContainerForSample}>
                      <p
                        className={classnames(classes.headline6, classes.title)}
                      >
                        {t('Sample E-PRO Numbers')}
                      </p>
                    </div>
                    {sampleGenarated.map((sample, index) => (
                      <p
                        key={index}
                        className={classnames(
                          classes.body1,
                          classes.eProSample
                        )}
                      >
                        {sample}
                      </p>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            }
            footer={
              <Fragment>
                <Button
                  className={classes.button}
                  onClick={handleReset}
                  color={'primary'}
                  classes={{ label: classes.buttonLabel }}
                >
                  {sampleGenarated ? t('Ok') : t('Cancel')}
                </Button>
                {!sampleGenarated && (
                  <ProgressButton
                    onClick={handleSubmit}
                    className={classes.button}
                    classes={{
                      disabled: classes.buttonDisabled,
                      label: classes.buttonLabel
                    }}
                    color={'primary'}
                    disabled={!dirty || isSubmitting}
                    isSubmitting={isSubmitting}
                  >
                    {type === FORM_TYPE.SAMPLE ? t('Generate') : t('Add')}
                  </ProgressButton>
                )}
              </Fragment>
            }
          />
        )}
      />
    );
  }
}

EProForm.contextType = AppContext;
const translatedEProForm = withNamespaces()(withApollo(EProForm));
export default withStyles(
  combineStyles(
    EProFormStyles,
    LayoutStyles,
    TypographyStyles,
    ButtonStyles,
    SpacingStyles
  )
)(translatedEProForm);
